/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect } from "react";
import { useState } from "react";
import { toast } from "react-toastify";
import { logEvent, INSURANCE_EVENTS } from "../../../utils/amplitude";

import { InsuranceCompany } from "../../../redux/types/InsuranceCompany";
import {
  CREATE_INSURANCE_COMPANY,
  EDIT_INSURANCE_COMPANY,
} from "../../../apolloClient/operation/mutations/mutation";
import { useMutation } from "@apollo/client";
import { CreateInsuranceCompanyInput } from "../../../types/graphQlQuery.interface";
import { Spacer, TxtInput } from "../../../components";
import styled from "@emotion/styled";
// Styled Components for Add/Edit Insurance Company
const PageWrapper = styled.div`
  width: 100%;
  min-height: 100%;
  background-color: #f8f9fa;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FormCard = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.06);
  padding: 32px 20px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FormTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  text-align: center;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-size: 0.98rem;
  color: #374151;
  font-weight: 500;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 10px;
`;

const PrimaryButton = styled.button`
  background: #4b87ed;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 28px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #3a6fd8;
  }
`;

const SecondaryButton = styled.button`
  background: #fff;
  color: #4b87ed;
  border: 1.5px solid #4b87ed;
  border-radius: 8px;
  padding: 10px 28px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #f3f4f6;
    color: #3a6fd8;
    border-color: #3a6fd8;
  }
`;

interface CreateCompanyProps {
  onTapAdd: Function;
  company?: InsuranceCompany;
  onCancel: Function;
}

export const AddCompany: React.FC<CreateCompanyProps> = ({
  onTapAdd,
  company,
  onCancel,
}) => {
  const [initialValues, setInitialValues] = useState<InsuranceCompany | null>(
    null
  );
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [logo, setLogo] = useState("");

  const [createInsuranceCompany] = useMutation<CreateInsuranceCompanyInput>(
    CREATE_INSURANCE_COMPANY,
    {
      onCompleted: () => {
        toast("Insurance Company  added!", { type: "success" });
        logEvent(INSURANCE_EVENTS.COMPANY_CREATED, { company_name: name, has_logo: !!logo });
        onTapAdd();
      },
      onError: (error) => {
        toast("Error creating insurance company!", { type: "error" });
        console.error("Error creating user:", error);
      },
    }
  );

  const [editInsuranceCompany] = useMutation<
    boolean,
    { id: number; input: CreateInsuranceCompanyInput }
  >(EDIT_INSURANCE_COMPANY, {
    onCompleted: () => {
      toast("Insurance Company updated successfully!", {
        type: "success",
        className: "toast-message",
      });
      
      // Track update with changed fields
      const fieldsUpdated = [];
      if (name !== initialValues?.companyName) fieldsUpdated.push('name');
      if (link !== initialValues?.claimLink) fieldsUpdated.push('link');
      if (logo !== initialValues?.companyLogo) fieldsUpdated.push('logo');
      logEvent(INSURANCE_EVENTS.COMPANY_UPDATED, { 
        company_id: company?.id || 0, 
        company_name: name, 
        fields_updated: fieldsUpdated 
      });
      
      onTapAdd();
    },
    onError: (error) => {
      toast("Error Updating insurance company!", {
        type: "error",
        className: "toast-message",
      });
      console.error("Error updating insurance company:", error);
    },
  });

  useEffect(() => {
    if (company) {
      setInitialValues(company);
      setName(company.companyName);
      setLink(company.claimLink);
      setLogo(company.companyLogo);
    }
  }, [company]);

  const getUpdatedFields = (): Partial<CreateInsuranceCompanyInput> => {
    const updatedFields: Partial<CreateInsuranceCompanyInput> = {};

    if (company && initialValues) {
      if (name !== initialValues.companyName) {
        updatedFields.companyName = name;
      }
      if (link !== initialValues.claimLink) {
        updatedFields.claimLink = link;
      }
      if (logo !== initialValues.companyLogo) {
        updatedFields.companyLogo = logo;
      }
    }

    return updatedFields;
  };

  const handleSubmitForm = async () => {
    if (!name || !link || !logo) {
      toast("Please enter mandatory fields!", {
        type: "error",
      });
      return;
    }

    const updatedFields = getUpdatedFields();

    if (company) {
      try {
        if (Object.keys(updatedFields).length === 0) {
          onTapAdd();
          toast("No changes made!", {
            type: "info",
          });
          return;
        }
        await editInsuranceCompany({
          variables: {
            id: company.id,
            input: updatedFields,
          },
        });
      } catch (error) {
        console.error("Error updating insurance company:", error);
      }
    } else {
      try {
        await createInsuranceCompany({
          variables: {
            input: {
              companyName: name,
              claimLink: link,
              companyLogo: logo,
            },
          },
        });
        onTapAdd();
      } catch (error) {
        console.error("Error creating insurance company:", error);
      }
    }
  };

  return (
    <PageWrapper>
      <FormCard>
        <FormTitle>
          {company ? "Edit Insurance Company" : "Add Insurance Company"}
        </FormTitle>
        <FormGroup>
          <FormLabel>Company Name *</FormLabel>
          <TxtInput
            value={name}
            placeholder="Enter company name"
            onChange={setName}
            background="white"
            width="100%"
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Claim Link *</FormLabel>
          <TxtInput
            value={link}
            placeholder="Enter claim link"
            onChange={setLink}
            background="white"
            width="100%"
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Company Logo *</FormLabel>
          <TxtInput
            value={logo}
            placeholder="Enter logo URL"
            onChange={setLogo}
            background="white"
            width="100%"
          />
        </FormGroup>
        <ButtonRow>
          <SecondaryButton onClick={() => onCancel()}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSubmitForm}>
            {company ? "Save" : "Create"}
          </PrimaryButton>
        </ButtonRow>
      </FormCard>
    </PageWrapper>
  );
};
